import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import {
    VStack,
    HStack,
    Text,
    Input,
    Button,
    IconButton,
    Checkbox,
    Box,
} from '@chakra-ui/react';
import { Plus, Trash2, Pencil, Check } from 'lucide-react';
import { useAppStore } from '../../state/store';
import { useWorksharingStore } from '../../state/useWorksharingStore';
import type { Workset } from '../../state/useWorksharingStore';
import { useBIMStore } from '../../state/useBIMStore';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';

/**
 * Worksets dialog (Collaborate > Manage Collaboration > Worksets).
 * BIM-style table: name, "Visible by default", owner/editable state and
 * assigned element counts. Supports New / Rename / Delete, opening a workset
 * for editing and assigning the current selection to the active workset.
 */
export function WorksetsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.collabDialog === 'worksets');
    const onClose = useAppStore((state) => state.closeCollabDialog);

    const worksets = useWorksharingStore((s) => s.worksets);
    const activeWorksetId = useWorksharingStore((s) => s.activeWorksetId);
    const assignments = useWorksharingStore((s) => s.assignments);
    const userName = useWorksharingStore((s) => s.userName);
    const worksharingEnabled = useWorksharingStore((s) => s.worksharingEnabled);
    const syncAssignmentsWithProject = useWorksharingStore((s) => s.syncAssignmentsWithProject);
    const addWorkset = useWorksharingStore((s) => s.addWorkset);
    const renameWorkset = useWorksharingStore((s) => s.renameWorkset);
    const deleteWorkset = useWorksharingStore((s) => s.deleteWorkset);
    const setWorksetVisibleByDefault = useWorksharingStore((s) => s.setWorksetVisibleByDefault);
    const setActiveWorkset = useWorksharingStore((s) => s.setActiveWorkset);
    const toggleWorksetEditable = useWorksharingStore((s) => s.toggleWorksetEditable);
    const assignElements = useWorksharingStore((s) => s.assignElements);

    const selection = useBIMStore((s) =>s.selection); const [newName, setNewName] = useState(''); const [newVisible, setNewVisible] = useState(true); const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');

    // When worksharing is on, make sure every project element has a workset
    // assignment before the table renders its counts (derived-state safety).
    useEffect(() => {
        if (isOpen && worksharingEnabled) syncAssignmentsWithProject();
    }, [isOpen, worksharingEnabled, syncAssignmentsWithProject]);

    const countFor = (ws: Workset) => {
        const n = Object.values(assignments).filter((w) => w === ws.id).length;
        // System worksets conceptually own datums that aren't tracked in
        // assignments; show "—" for them so the table never looks broken.
        return ws.isSystem ? (n > 0 ? `${n}` : '—') : `${n}`;
    };

    const handleAdd = () => {
        const id = addWorkset(newName, newVisible);
        if (id) setNewName('');
    };

    const handleCommitRename = () => {
        if (editingId && editingName.trim()) renameWorkset(editingId, editingName.trim());
        setEditingId(null);
    };

    return (
        <ManageDialogFrame
            title={t('collab.worksets')}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <Button
                            size="sm"
                            variant="outline"
                            isDisabled={selection.length === 0 || !activeWorksetId}
                            onClick={() => assignElements(selection, activeWorksetId)}
                        >
                            {selection.length > 0
                                ? `Assign ${selection.length} Element(s) to Active`
                                : 'Assign Selection to Active'}
                        </Button>
                    }
                />
            }
        >
            <VStack spacing={3} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">{t('collab.worksetsGroupModelElementsSoTeamMembersCanOwnPortionsOfTheProjectT')}</Text>

                {/* Table */}
                <Box
                    borderWidth="1px"
                    borderColor="var(--color-border)"
                    borderRadius="var(--cad-radius-sm)"
                    overflowY="auto"
                    maxH="300px"
                >
                    <HStack
                        px={3}
                        py={1.5}
                        bg="var(--color-bg-tertiary)"
                        borderBottom="1px solid var(--color-border)"
                        spacing={3}
                        fontSize="xs"
                        fontWeight="semibold"
                        color="var(--color-text-secondary)"
                    >
                        <Box flex="1 1 auto" minW={0}>{t('collab.name')}</Box>
                        <Box w="120px" flexShrink={0} textAlign="center">{t('collab.visibleByDefault')}</Box>
                        <Box w="130px" flexShrink={0} textAlign="center">{t('collab.statusOwner')}</Box>
                        <Box w="64px" flexShrink={0} textAlign="right">{t('collab.elements')}</Box>
                        <Box w="88px" flexShrink={0} />
                    </HStack>
                    {worksets.map((ws) => {
                        const isActive = ws.id === activeWorksetId;
                        const mineOwner = ws.owner === userName;
                        return (
                            <HStack
                                key={ws.id}
                                px={3}
                                py={1.5}
                                spacing={3}
                                borderBottom="1px solid var(--color-border)"
                                bg={isActive ? 'var(--color-bg-tertiary)' : 'transparent'}
                            >
                                <Box flex="1 1 auto" minW={0}>
                                    {editingId === ws.id ? (
                                        <Input
                                            size="xs"
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleCommitRename();
                                                if (e.key === 'Escape') setEditingId(null);
                                            }}
                                            autoFocus
                                            bg="var(--color-bg-primary)"
                                            borderColor="var(--color-accent)"
                                        />
                                    ) : (
                                        <HStack spacing={1.5} minW={0}>
                                            <Text
                                                as="button"
                                                fontSize="sm"
                                                fontWeight={isActive ? 'bold' : 'normal'}
                                                color={isActive ? 'var(--color-accent)' : 'inherit'}
                                                whiteSpace="nowrap"
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                onClick={() => setActiveWorkset(ws.id)}
                                                title={t('collab.setAsActiveWorkset')}
                                            >
                                                {ws.name}
                                            </Text>
                                            {isActive && (
                                                <Text fontSize="2xs" color="var(--color-accent)" flexShrink={0}>{t('collab.active')}</Text>
                                            )}
                                        </HStack>
                                    )}
                                </Box>
                                <Box w="120px" flexShrink={0} textAlign="center">
                                    <Checkbox
                                        size="sm"
                                        isChecked={ws.visibleByDefault}
                                        isDisabled={ws.isSystem}
                                        onChange={(e) => setWorksetVisibleByDefault(ws.id, e.target.checked)}
                                    >
                                        <Text fontSize="xs">{t('collab.nbsp')}</Text>
                                    </Checkbox>
                                </Box>
                                <Box w="130px" flexShrink={0} textAlign="center" fontSize="xs">
                                    {ws.owner
                                        ? <Text color={mineOwner ? 'var(--color-accent)' : 'var(--color-text-secondary)'} isTruncated>{mineOwner ? 'Editable by me' : `Editable by ${ws.owner}`}</Text>
                                        : <Text color="var(--color-text-secondary)">—</Text>}
                                </Box>
                                <Box w="64px" flexShrink={0} textAlign="right" fontSize="xs">{countFor(ws)}</Box>
                                <HStack w="88px" flexShrink={0} justify="flex-end" spacing={1}>
                                    {editingId === ws.id ? (
                                        <IconButton aria-label={t('collab.commitRename')} icon={<Check size={13} />} size="xs" variant="ghost" onClick={handleCommitRename} />
                                    ) : (
                                        <>
                                            {!ws.isSystem && (
                                                <IconButton
                                                    aria-label={t('collab.renameWorkset')}
                                                    icon={<Pencil size={13} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => { setEditingId(ws.id); setEditingName(ws.name); }}
                                                />
                                            )}
                                            <IconButton
                                                aria-label={ws.owner ? 'Close workset' : 'Open workset for editing'}
                                                icon={ws.owner ? <Check size={13} /> : <Pencil size={13} />}
                                                size="xs"
                                                variant="ghost"
                                                isDisabled={!ws.isSystem && ws.owner !== null && !mineOwner}
                                                onClick={() => toggleWorksetEditable(ws.id)}
                                            />
                                            {!ws.isSystem && (
                                                <IconButton
                                                    aria-label={t('collab.deleteWorkset')}
                                                    icon={<Trash2 size={13} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="var(--color-error)"
                                                    onClick={() => deleteWorkset(ws.id)}
                                                />
                                            )}
                                        </>
                                    )}
                                </HStack>
                            </HStack>
                        );
                    })}
                </Box>

                {/* New workset row */}
                <HStack spacing={2}>
                    <Input
                        size="sm"
                        placeholder={t('collab.newWorksetName')}
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                        bg="var(--color-bg-primary)"
                        borderColor="var(--color-border)"
                        flex="1 1 auto"
                        minW={0}
                    />
                    <Checkbox size="sm" isChecked={newVisible} onChange={(e) => setNewVisible(e.target.checked)}>
                        <Text fontSize="xs" whiteSpace="nowrap">{t('collab.visibleByDefault')}</Text>
                    </Checkbox>
                    <Button size="sm" leftIcon={<Plus size={14} />} onClick={handleAdd} isDisabled={!newName.trim()}>{t('collab.new')}</Button>
                </HStack>
            </VStack>
        </ManageDialogFrame>
    );
}
